import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Row, Col, Spinner } from "reactstrap";
import { RiUserVoiceLine } from "react-icons/ri";
import Api from "../../components/helpers/Api";
import { SeoHelper } from "../../components/helpers/SeoHelper";

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

function toSlug(name) {
    return name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');
}

function getSurname(name) {
    const parts = name.trim().split(/\s+/);
    return parts[parts.length - 1];
}

function toDisplayName(name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return name;
    const surname = parts[parts.length - 1];
    const firstNames = parts.slice(0, -1).join(' ');
    return `${surname} ${firstNames}`;
}

function toSortKey(name) {
    return toDisplayName(name).toLowerCase();
}

export default function AuthorsByLetterPage() {

    const { segment } = useParams();
    const letter = segment.toLowerCase();

    const [authors, setAuthors] = useState(null);

    useEffect(() => {
        Api.get("/api/authors")
            .then(res => {
                const all = res.data || [];
                const filtered = all
                    .filter(a => getSurname(a.name).toLowerCase().startsWith(letter))
                    .sort((a, b) => toSortKey(a.name).localeCompare(toSortKey(b.name)));
                setAuthors(filtered);
            })
            .catch(err => {
                console.error("Failed to load authors", err);
                setAuthors([]);
            });
    }, [letter]);

    return (
        <>
            <SeoHelper
                uri={`/authors/${letter}`}
                title={`Worship Song Authors — ${letter.toUpperCase()}`}
                description={`Browse worship song authors whose surname begins with ${letter.toUpperCase()}.`}
            />

            <section className="section section-lg">

                <Container>

                    <Row className="justify-content-center">

                        <Col lg="8">

                            <h1 className="display-3 text-center text-primary">
                                Authors — {letter.toUpperCase()}
                            </h1>

                            <p className="lead text-center text-muted mb-4">
                                Worship song authors whose surname begins with {letter.toUpperCase()}.
                            </p>

                            <div className="d-flex flex-wrap justify-content-center mb-5" style={{ gap: '6px' }}>
                                {LETTERS.map(l => (
                                    <Link
                                        key={l}
                                        to={`/authors/${l.toLowerCase()}`}
                                        className={`btn btn-sm ${l.toLowerCase() === letter ? 'btn-primary' : 'btn-outline-primary'}`}
                                        style={{ width: '40px', fontWeight: 600 }}
                                    >
                                        {l}
                                    </Link>
                                ))}
                            </div>

                            {authors === null ? (
                                <div className="text-center mt-5">
                                    <Spinner color="primary" />
                                </div>
                            ) : authors.length === 0 ? (
                                <p className="text-center text-muted">
                                    No authors found for this letter.
                                </p>
                            ) : (
                                <ul className="twb-next-steps">
                                    {authors.map(author => (
                                        <li key={author.id}>
                                            <Link
                                                to={`/authors/${author.id}/${toSlug(author.name)}`}
                                                className="twb-next-step"
                                            >
                                                <div className="twb-next-step-icon">
                                                    <RiUserVoiceLine size={24} />
                                                </div>
                                                <div className="twb-next-step-text">
                                                    {toDisplayName(author.name)}
                                                </div>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <p className="text-center text-muted mt-5">
                                <Link to="/authors">← Back to all authors</Link>
                            </p>

                        </Col>

                    </Row>

                </Container>

            </section>
        </>
    );
}
